import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  AuthStatusResponse,
  tenantAuthStatusApiClient,
} from '@src/api/tenant/auth/tenantAuthStatusApiClient';
import { SERVICE, ServiceType } from '@src/api/types/shared/serviceType';
import { SESSION_STORAGE_KEYS } from '@src/constants/sessionStorage';
import { TenantRoleType } from '@src/enum/tenantRoleEnum';
import Error404 from '@src/pages/Error404';
import { TENANT_URL } from '@src/routes/tenant';
import { isValidDxstarUrl } from '@src/utils/redirectAfterLogin';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  children: React.ReactNode;
  canRoles: TenantRoleType[];
  canService?: ServiceType[];
};

const TenantRoleGuard: React.FC<Props> = ({ children, canRoles, canService }) => {
  const [authStatusResponse, setAuthStatusResponse] = useState<AuthStatusResponse | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAuthStatus = async () => {
      const res = await tenantAuthStatusApiClient.status();
      handleApiResponse(res, (data) => {
        setAuthStatusResponse(data);
      });
    };

    fetchAuthStatus();
  }, []);

  if (authStatusResponse === null) {
    return null;
  }

  if (!authStatusResponse.is_login) {
    if (window.location.pathname === TENANT_URL.LOGIN) {
      return null;
    }

    const currentUrl = window.location.pathname + window.location.search;

    if (!isValidDxstarUrl(currentUrl)) {
      console.error('Invalid redirect URL');
      return null;
    }

    sessionStorage.setItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN, currentUrl);
    navigate(`${TENANT_URL.LOGIN}?redirect=${encodeURIComponent(currentUrl)}`);
    return null;
  }

  const hasRequiredRole = canRoles.some((role) => authStatusResponse.user?.role.code === role);
  if (!hasRequiredRole || !authStatusResponse.user?.enabled) {
    return <Error404 isTenant={true} />;
  }

  if (canService) {
    if (!authStatusResponse.tenant) {
      return null;
    }

    const hasRequiredService = canService.some((service) => {
      if (service === SERVICE.ENTERPRISE_PORTAL) {
        return authStatusResponse?.tenant?.service?.enterprise_portal;
      }
      if (service === SERVICE.DX_DIAGNOSIS) {
        return authStatusResponse?.tenant?.service?.dx_diagnosis;
      }
      if (service === SERVICE.ONLINE_COMMUNITY) {
        return authStatusResponse?.tenant?.service?.online_community;
      }

      return false;
    });

    if (!hasRequiredService) {
      return <Error404 isTenant={false} />;
    }
  }

  return <>{children}</>;
};

export default TenantRoleGuard;
